import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Edit, Phone, Mail, MapPin, Globe, User, FileText, Star, Save, X, Calendar, Wallet, TrendingDown, TrendingUp, Building2, Download, Scale, ChevronRight } from 'lucide-react';
import { Button, Card, CardStats, Table, StatusBadge, ExportDropdown, MediaUpload } from '@components/ui';
import { useExportExcel, useExportPDF } from '@hooks';
import { getCompanyById } from '@mocks/firmalar';
import { mockChecks, getBankLabel } from '@mocks/cekler';
import { getCariAccountByCompanyId, getMovementsByAccountId, MOVEMENT_SUBTYPE_LABELS } from '@mocks/cariHesaplar';
import CariHesapDetay from '@pages/Finans/CariHesaplar/CariHesapDetay';

export default function CompanyDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [documents, setDocuments] = useState([
        { id: 'doc-1', name: 'Taseron_Taslak_Sozlesme.pdf', type: 'application/pdf', size: 1548000, date: '2025-08-10' }
    ]);

    const company = getCompanyById(id);
    const cariAccount = getCariAccountByCompanyId(id);
    const [cariDetayOpen, setCariDetayOpen] = useState(false);

    const [formData, setFormData] = useState({
        name: company?.name || '',
        shortName: company?.shortName || '',
        phone: company?.phone || '',
        email: company?.email || '',
        taxNo: company?.taxNo || '',
        taxOffice: company?.taxOffice || '',
        address: company?.address || '',
        website: company?.website || '',
        contactPerson: company?.contactPerson || '',
        contactPhone: company?.contactPhone || '',
        specialty: company?.specialty || '',
        notes: company?.notes || '',
    });

    if (!company) {
        return (
            <div className="p-6 text-center">
                <p className="text-text-tertiary">Firma bulunamadı.</p>
                <Button variant="ghost" onClick={() => navigate('/firmalar')} className="mt-4">
                    Geri Dön
                </Button>
            </div>
        );
    }

    // Export
    const reportRef = useRef(null);
    const { exportToExcel, isExporting: isExportingExcel } = useExportExcel();
    const { exportToPDF, isExporting: isExportingPDF } = useExportPDF();

    const handleExportExcel = () => {
        exportToExcel({
            data: (company.payments || []).map(p => ({
                'Tarih': p.date,
                'Tip': p.type,
                'Tutar': p.amount,
                'Açıklama': p.description,
            })),
            columns: [
                { header: 'Tarih', key: 'Tarih' },
                { header: 'Tip', key: 'Tip' },
                { header: 'Tutar', key: 'Tutar', format: 'currency' },
                { header: 'Açıklama', key: 'Açıklama' },
            ],
            fileName: `firma_${company.shortName || company.name}`,
            sheetName: 'Ödemeler',
        });
    };

    const handleExportPDF = () => {
        exportToPDF({
            elementRef: reportRef,
            fileName: `firma_${company.shortName || company.name}`,
            title: `Firma Kartı - ${company.name}`,
            orientation: 'portrait',
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        console.log('Kaydedildi:', formData);
        toast.success('Firma bilgileri güncellendi');
        setIsEditing(false);
    };

    const handleDocumentsChange = (files) => {
        // Mock upload — sync MediaUpload selection with documents state
        setDocuments(prev => {
            const savedDocs = prev.filter(d => !d._pending);
            const newDocs = files.map(file => ({
                id: `doc-pending-${file.name}`,
                name: file.name,
                type: file.type,
                size: file.size,
                date: new Date().toISOString().split('T')[0],
                _pending: true,
            }));
            return [...savedDocs, ...newDocs];
        });
    };

    const documentColumns = [
        {
            key: 'name', header: 'Belge Adı', width: 'lg',
            render: (val, row) => (
                <div className="flex items-center gap-3">
                    <FileText size={18} className={row.type.includes('pdf') ? "text-error" : "text-primary"} />
                    <span className="font-medium text-text-primary">{val}</span>
                </div>
            )
        },
        { key: 'date', header: 'Yüklenme Tarihi', width: 'md', render: (val) => formatDate(val) },
        { key: 'size', header: 'Boyut', width: 'sm', render: (val) => `${(val / 1024 / 1024).toFixed(2)} MB` },
        {
            key: 'actions', header: '', width: 'sm', align: 'right',
            render: () => (
                <Button variant="ghost" size="xs" icon={<Download size={16} />}>İndir</Button>
            )
        },
    ];

    const contractColumns = [
        { key: 'title', header: 'Sözleşme', width: 'lg', render: (val) => <span className="font-medium">{val}</span> },
        { key: 'constructionName', header: 'İnşaat', width: 'lg' },
        { key: 'startDate', header: 'Başlangıç', width: 'md', render: (val) => formatDate(val) },
        { key: 'endDate', header: 'Bitiş', width: 'md', render: (val) => formatDate(val) },
        { key: 'totalAmount', header: 'Tutar', width: 'md', align: 'right', render: (val) => formatCurrency(val) },
        {
            key: 'status', header: 'Durum', width: 'sm', render: (val) => {
                const labels = { active: 'Aktif', completed: 'Tamamlandı', pending: 'Beklemede' };
                const variants = { active: 'success', completed: 'tertiary', pending: 'warning' };
                return <StatusBadge status={labels[val]} variant={variants[val]} />;
            }
        },
    ];

    const paymentColumns = [
        { key: 'date', header: 'Tarih', width: 'md', render: (val) => formatDate(val) },
        {
            key: 'type', header: 'Tip', width: 'sm', render: (val) => (
                <StatusBadge status={val} variant={val === 'hakedis' ? 'info' : val === 'avans' ? 'warning' : 'secondary'} />
            )
        },
        {
            key: 'amount', header: 'Tutar', width: 'md', align: 'right', render: (val) => (
                <span className="text-error font-medium">{formatCurrency(val)}</span>
            )
        },
        { key: 'description', header: 'Açıklama', width: 'lg' },
    ];

    const companyChecks = mockChecks.filter(c => c.supplierId === company?.id || c.recipient === company?.name);

    const checkColumns = [
        { key: 'checkNo', header: 'Çek No', width: 'md', render: (val) => <span className="font-mono text-sm font-medium">{val}</span> },
        { key: 'dueDate', header: 'Vade', width: 'md', render: (val) => formatDate(val) },
        {
            key: 'type', header: 'Tür', width: 'sm', render: (val) => (
                <StatusBadge status={val === 'verilen' ? 'Verilen' : 'Alınan'} variant={val === 'verilen' ? 'error' : 'success'} />
            )
        },
        { key: 'amount', header: 'Tutar', width: 'md', align: 'right', render: (val) => <span className="font-bold">{formatCurrency(val)}</span> },
        {
            key: 'status', header: 'Durum', width: 'sm', render: (val) => {
                const labels = { bekliyor: 'Bekliyor', odendi: 'Ödendi', tahsil: 'Tahsil Edildi', ciro_edildi: 'Ciro Edildi', iade: 'İade', gecikti: 'Gecikti' };
                const variants = { bekliyor: 'warning', odendi: 'primary', tahsil: 'success', ciro_edildi: 'info', iade: 'neutral', gecikti: 'error' };
                return <StatusBadge status={labels[val] || val} variant={variants[val] || 'default'} />;
            }
        }
    ];

    // Mobile Contract Card View
    const ContractCardView = () => (
        <div className="space-y-3 md:hidden">
            {company.contracts?.map((contract) => {
                const statusLabels = { active: 'Aktif', completed: 'Tamamlandı', pending: 'Beklemede' };
                const statusVariants = { active: 'success', completed: 'tertiary', pending: 'warning' };
                return (
                    <div key={contract.id} className="bg-bg-tertiary rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                                <p className="font-semibold text-text-primary truncate">{contract.title}</p>
                                <p className="text-xs text-text-tertiary">{contract.constructionName}</p>
                            </div>
                            <StatusBadge status={statusLabels[contract.status]} variant={statusVariants[contract.status]} size="xs" />
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-text-muted">{formatDate(contract.startDate)} - {formatDate(contract.endDate)}</span>
                            <span className="font-bold text-text-primary">{formatCurrency(contract.totalAmount)}</span>
                        </div>
                    </div>
                );
            })}
            {(!company.contracts || company.contracts.length === 0) && (
                <p className="text-center text-text-tertiary py-4">Sözleşme bulunamadı.</p>
            )}
        </div>
    );

    // Mobile Payment Card View
    const PaymentCardView = () => (
        <div className="space-y-3 md:hidden">
            {company.payments?.map((payment) => (
                <div key={payment.id} className="bg-bg-tertiary rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-error-100 flex items-center justify-center">
                                <TrendingDown size={16} className="text-error" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-text-primary">{formatDate(payment.date)}</p>
                                <StatusBadge status={payment.type} variant={payment.type === 'hakedis' ? 'info' : payment.type === 'avans' ? 'warning' : 'secondary'} size="xs" />
                            </div>
                        </div>
                        <span className="text-error font-bold">{formatCurrency(payment.amount)}</span>
                    </div>
                    <p className="text-sm text-text-muted">{payment.description}</p>
                </div>
            ))}
            {(!company.payments || company.payments.length === 0) && (
                <p className="text-center text-text-tertiary py-4">Ödeme kaydı bulunamadı.</p>
            )}
        </div>
    );

    // Mobile Check Card View
    const CheckCardView = () => (
        <div className="space-y-3 md:hidden">
            {companyChecks.map((check) => {
                const statusLabels = { bekliyor: 'Bekliyor', odendi: 'Ödendi', tahsil: 'Tahsil Edildi', ciro_edildi: 'Ciro Edildi', iade: 'İade', gecikti: 'Gecikti' };
                const statusVariants = { bekliyor: 'warning', odendi: 'primary', tahsil: 'success', ciro_edildi: 'info', iade: 'neutral', gecikti: 'error' };
                return (
                    <div key={check.id} className="bg-bg-tertiary rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                                <p className="font-semibold text-text-primary truncate font-mono text-sm">{check.checkNo}</p>
                                <p className="text-xs text-text-tertiary">{formatDate(check.dueDate)}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                <StatusBadge status={statusLabels[check.status] || check.status} variant={statusVariants[check.status] || 'default'} size="xs" />
                                <StatusBadge status={check.type === 'verilen' ? 'Verilen' : 'Alınan'} variant={check.type === 'verilen' ? 'error' : 'success'} size="xs" />
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-3 pt-3 border-t border-border-light">
                            <span className="text-text-muted">{getBankLabel(check.bank)}</span>
                            <span className="font-bold text-text-primary">{formatCurrency(check.amount)}</span>
                        </div>
                    </div>
                );
            })}
            {companyChecks.length === 0 && (
                <p className="text-center text-text-tertiary py-4">Kayıtlı çek bulunamadı.</p>
            )}
        </div>
    );

    return (
        <div className="p-4 md:p-6 space-y-6" ref={reportRef}>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                    <Button variant="ghost" onClick={() => navigate('/firmalar')} className="min-w-[44px] min-h-[44px] p-2">
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl sm:text-2xl font-bold text-text-primary">{company.shortName || company.name}</h1>
                            <div className="flex items-center gap-1 text-warning">
                                <Star size={16} className="fill-warning" />
                                <span className="text-sm font-medium">{company.rating}</span>
                            </div>
                        </div>
                        <p className="text-text-tertiary text-sm sm:text-base">{company.typeLabel} • {company.specialty}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <ExportDropdown
                        onExportExcel={handleExportExcel}
                        onExportPDF={handleExportPDF}
                        isExporting={isExportingExcel || isExportingPDF}
                    />
                    {isEditing ? (
                        <>
                            <Button variant="ghost" onClick={() => setIsEditing(false)} icon={<X size={18} />} className="min-h-[44px]">İptal</Button>
                            <Button variant="success" onClick={handleSave} icon={<Save size={18} />} className="min-h-[44px]">Kaydet</Button>
                        </>
                    ) : (
                        <Button variant="primary" onClick={() => setIsEditing(true)} icon={<Edit size={18} />} className="min-h-[44px]">Düzenle</Button>
                    )}
                </div>
            </div>

            {/* Stats — cariAccount'tan canlı veri */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <CardStats
                    title="Cari Bakiye"
                    value={cariAccount ? formatCurrency(cariAccount.currentBalance) : formatCurrency(company.totalRemaining)}
                    icon={Scale}
                    color={cariAccount ? (cariAccount.currentBalance > 0 ? 'error' : 'success') : (company.totalRemaining > 0 ? 'warning' : 'success')}
                    size="sm"
                />
                <CardStats
                    title="Toplam Alış"
                    value={cariAccount ? formatCurrency(cariAccount.totalPurchases) : formatCurrency(company.totalPaid)}
                    icon={TrendingUp}
                    color="error"
                    size="sm"
                />
                <CardStats
                    title="Aktif Sözleşme"
                    value={company.contracts?.filter(c => c.status === 'active').length || 0}
                    icon={FileText}
                    color="success"
                    size="sm"
                />
                <CardStats
                    title="Çek Portföyü"
                    value={`${companyChecks.length} adet`}
                    icon={Wallet}
                    color={companyChecks.filter(c => c.status === 'bekliyor').length > 0 ? 'warning' : 'tertiary'}
                    size="sm"
                />
            </div>

            {/* Cari Hesap — en kritik finansal özet, stats'ın hemen altında */}
            {cariAccount && (
                <Card padding="lg">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                            <Scale size={20} className="text-error" /> Cari Hesap
                        </h3>
                        <Button
                            variant="ghost"
                            size="sm"
                            icon={<ChevronRight size={16} />}
                            iconRight
                            onClick={() => setCariDetayOpen(true)}
                        >
                            Tüm Hareketler
                        </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-error/5 border border-error/20 rounded-xl p-3 text-center">
                            <p className="text-xs text-text-tertiary mb-1">Toplam Alış</p>
                            <p className="text-base font-bold text-error">{formatCurrency(cariAccount.totalPurchases)}</p>
                        </div>
                        <div className="bg-success/5 border border-success/20 rounded-xl p-3 text-center">
                            <p className="text-xs text-text-tertiary mb-1">Toplam Ödeme</p>
                            <p className="text-base font-bold text-success">{formatCurrency(cariAccount.totalPayments)}</p>
                        </div>
                        <div className={`rounded-xl p-3 text-center border ${cariAccount.currentBalance > 0 ? 'bg-error/10 border-error/30' : 'bg-success/10 border-success/30'}`}>
                            <p className="text-xs text-text-tertiary mb-1">Güncel Bakiye</p>
                            <p className={`text-base font-bold ${cariAccount.currentBalance > 0 ? 'text-error' : 'text-success'}`}>
                                {cariAccount.currentBalance > 0 ? formatCurrency(cariAccount.currentBalance) : 'Kapalı'}
                            </p>
                        </div>
                    </div>
                    {/* Son 3 hareket */}
                    {(() => {
                        const movements = getMovementsByAccountId(cariAccount.id).slice(-3).reverse();
                        if (!movements.length) return <p className="text-sm text-text-tertiary text-center py-2">Hareket bulunmuyor.</p>;
                        return (
                            <div className="space-y-2">
                                {movements.map(m => (
                                    <div key={m.id} className="flex items-center justify-between text-sm py-2 border-b border-border-light last:border-0">
                                        <div className="flex items-center gap-2">
                                            {m.type === 'purchase'
                                                ? <TrendingUp size={14} className="text-error shrink-0" />
                                                : <TrendingDown size={14} className="text-success shrink-0" />
                                            }
                                            <div>
                                                <p className="font-medium text-text-primary">{m.description}</p>
                                                <p className="text-xs text-text-tertiary">
                                                    {new Date(m.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    {' · '}{MOVEMENT_SUBTYPE_LABELS[m.subtype]}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`font-semibold ${m.type === 'purchase' ? 'text-error' : 'text-success'}`}>
                                            {m.type === 'purchase' ? '+' : '-'}{formatCurrency(m.amount)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </Card>
            )}

            {/* Sözleşmeler */}
            <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                    <FileText size={20} /> Sözleşmeler
                    <span className="text-sm font-normal text-text-tertiary">({company.contracts?.length || 0})</span>
                </h3>
                {/* Mobile Card View */}
                <ContractCardView />
                {/* Desktop Table View */}
                <div className="hidden md:block">
                    <Table
                        columns={contractColumns}
                        data={company.contracts || []}
                        compact
                        emptyMessage="Sözleşme bulunamadı."
                        emptyIcon={<FileText size={32} />}
                    />
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {/* Çek Portföyü */}
                <Card padding="lg">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Wallet size={20} /> Çek Portföyü
                        <span className="text-sm font-normal text-text-tertiary">({companyChecks.length})</span>
                    </h3>
                    <CheckCardView />
                    <div className="hidden md:block">
                        <Table
                            columns={checkColumns}
                            data={companyChecks}
                            compact
                            emptyMessage="Kayıtlı çek bulunamadı."
                            emptyIcon={<Wallet size={32} />}
                        />
                    </div>
                </Card>

                {/* Hakediş / Avans Geçmişi */}
                <Card padding="lg">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Wallet size={20} /> Hakediş / Avans Geçmişi
                        <span className="text-sm font-normal text-text-tertiary">({company.payments?.length || 0})</span>
                    </h3>
                    <PaymentCardView />
                    <div className="hidden md:block">
                        <Table
                            columns={paymentColumns}
                            data={company.payments || []}
                            compact
                            emptyMessage="Ödeme kaydı bulunamadı."
                            emptyIcon={<Wallet size={32} />}
                        />
                    </div>
                </Card>
            </div>

            {/* Firma Bilgileri */}
            <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Firma Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {isEditing ? (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Firma Adı</label>
                                <input type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.name} onChange={(e) => handleChange('name', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Kısa Ad</label>
                                <input type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.shortName} onChange={(e) => handleChange('shortName', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Telefon</label>
                                <input type="tel" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">E-posta</label>
                                <input type="email" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.email} onChange={(e) => handleChange('email', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Vergi No</label>
                                <input type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.taxNo} onChange={(e) => handleChange('taxNo', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">Vergi Dairesi</label>
                                <input type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.taxOffice} onChange={(e) => handleChange('taxOffice', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">İletişim Kişisi</label>
                                <input type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.contactPerson} onChange={(e) => handleChange('contactPerson', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-text-secondary mb-1">İletişim Telefonu</label>
                                <input type="tel" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.contactPhone} onChange={(e) => handleChange('contactPhone', e.target.value)} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text-secondary mb-1">Adres</label>
                                <input type="text" className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-base"
                                    value={formData.address} onChange={(e) => handleChange('address', e.target.value)} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text-secondary mb-1">Notlar</label>
                                <textarea className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary resize-none text-base"
                                    rows={3} value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <Phone size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">Telefon</p>
                                    <p className="text-text-primary truncate">{company.phone}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <Mail size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">E-posta</p>
                                    <p className="text-text-primary truncate">{company.email || '-'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <User size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">İletişim Kişisi</p>
                                    <p className="text-text-primary truncate">{company.contactPerson} - {company.contactPhone}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <Globe size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">Web Sitesi</p>
                                    <p className="text-text-primary truncate">{company.website || '-'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <FileText size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">Vergi Bilgileri</p>
                                    <p className="text-text-primary truncate">{company.taxNo} - {company.taxOffice}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg">
                                <MapPin size={18} className="text-text-muted shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-text-tertiary">Adres</p>
                                    <p className="text-text-primary truncate">{company.address}</p>
                                </div>
                            </div>
                            {company.notes && (
                                <div className="md:col-span-2">
                                    <p className="text-sm text-text-tertiary mb-1">Notlar</p>
                                    <p className="text-text-primary bg-bg-tertiary p-3 rounded-lg">{company.notes}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Card>

            {/* Ekli Belgeler */}
            <Card padding="lg">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                        <FileText size={20} /> Ekli Belgeler
                        <span className="text-sm font-normal text-text-tertiary">({documents.length})</span>
                    </h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Upload Area */}
                    <div className="lg:col-span-1">
                        <MediaUpload
                            mode="multiple"
                            accept="all"
                            label="Yeni Belge Yükle"
                            hint="Sözleşme, imza sirküleri, vb. PDF veya görsel"
                            maxSizeMB={10}
                            maxFiles={8}
                            onFilesChange={handleDocumentsChange}
                        />
                    </div>
                    {/* Documents List */}
                    <div className="lg:col-span-2">
                        {documents.length > 0 ? (
                            <Table
                                columns={documentColumns}
                                data={documents}
                                compact
                            />
                        ) : (
                            <div className="h-full min-h-[200px] flex items-center justify-center border-2 border-dashed border-border-light rounded-xl">
                                <p className="text-text-tertiary text-sm">Henüz belge yüklenmemiş.</p>
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* Cari Hesap Detay Panel */}
            {cariDetayOpen && cariAccount && (
                <CariHesapDetay
                    account={cariAccount}
                    constructionId={null}
                    onClose={() => setCariDetayOpen(false)}
                    onMovementAdded={() => setCariDetayOpen(false)}
                />
            )}
        </div>
    );
}

